"use client";

import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";
import { Typography } from "./typography";

type Option = { label: string; value: string };

type CheckAll = { label: string; inGroup?: boolean; className?: string };

type CheckboxGroupProps = {
  label?: string;
  options: Option[] | string[];
  values: string[];
  onChange: (updated: string[]) => void;
  checkAllLabel?: CheckAll;
  className?: string;
};

export default function CheckboxGroup({
  label,
  options,
  values,
  onChange,
  checkAllLabel,
  className,
}: CheckboxGroupProps) {
  const allValues = options.map((o) => (typeof o === "string" ? o : o.value));
  const allChecked = allValues.every((v) => values.includes(v));
  const isIndeterminate = values.length > 0 && values.length < allValues.length;

  const handleToggle = (value: string) => {
    const updated = values.includes(value)
      ? values.filter((v) => v !== value)
      : [...values, value];
    onChange(updated);
  };

  const handleCheckAll = (checked: boolean) => {
    onChange(checked ? allValues : []);
  };

  const checkAllRender = (checkAllLabel: CheckAll) => {
    return (
      <label
        className={cn(
          "flex items-center space-x-2",
          !checkAllLabel.inGroup ? "mb-2" : "",
          checkAllLabel.className,
        )}
      >
        <Checkbox
          checked={allChecked}
          onCheckedChange={handleCheckAll}
          indeterminate={isIndeterminate}
          className="border-secondary-800 shadow-none"
        />
        <Typography variant="detail1" tone="medium">
          {checkAllLabel.label}
        </Typography>
      </label>
    );
  };

  return (
    <div className="flex-1">
      {label && <Label className="mb-2 block">{label}</Label>}

      {/* Check All */}
      {checkAllLabel && !checkAllLabel.inGroup && checkAllRender(checkAllLabel)}

      {/* Options */}
      <div className={cn("grid grid-cols-3 gap-2", className)}>
        {checkAllLabel &&
          checkAllLabel.inGroup &&
          checkAllRender(checkAllLabel)}
        {options.map((item) => {
          let opt: Option;
          if (typeof item === "string") {
            opt = { value: item, label: item };
          } else {
            opt = item;
          }
          return (
            <label
              key={opt.value}
              className="flex items-center text-xs space-x-2"
            >
              <Checkbox
                checked={values.includes(opt.value)}
                onCheckedChange={() => handleToggle(opt.value)}
                id={`chk-${opt.value}`}
                className="border-secondary-800 shadow-none"
              />
              <Typography variant="detail1" tone="medium">
                {opt.label}
              </Typography>
            </label>
          );
        })}
      </div>
    </div>
  );
}
